import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const AuditTrailPanel = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');

  const auditTrail = [
    {
      id: 1,
      timestamp: '2025-08-19 04:15:23',
      action: 'Asset Verified',
      assetId: 'AT-2025-0156',
      user: 'Arjun Sharma',
      userRole: 'Audit Supervisor',
      details: 'Asset location confirmed, condition verified as Good',
      changes: {
        status: { from: 'Pending Verification', to: 'Verified' },
        location: { from: 'Unknown', to: 'Floor 2 - Room 201' }
      },
      ipAddress: '192.168.1.45',
      sessionId: 'sess_abc123'
    },
    {
      id: 2,
      timestamp: '2025-08-19 03:42:17',
      action: 'Bulk Status Update',
      assetId: 'Multiple (15 assets)',
      user: 'Priya Patel',
      userRole: 'IT Administrator',
      details: 'Bulk verification of laptop assets in Finance department',
      changes: {
        status: { from: 'Pending', to: 'In Progress' }
      },
      ipAddress: '192.168.1.67',
      sessionId: 'sess_def456'
    },
    {
      id: 3,
      timestamp: '2025-08-19 02:28:45',
      action: 'Exception Escalated',
      assetId: 'AT-2025-0089',
      user: 'Rahul Verma',
      userRole: 'Auditor',
      details: 'Asset not found at registered location, escalated to department manager',
      changes: {
        status: { from: 'Pending', to: 'Exception - Missing' },
        escalationLevel: { from: 'None', to: 'Department Manager' }
      },
      ipAddress: '192.168.1.23',
      sessionId: 'sess_ghi789'
    },
    {
      id: 4,
      timestamp: '2025-08-19 01:55:12',
      action: 'Asset Created',
      assetId: 'AT-2025-0345',
      user: 'Anjali Singh',
      userRole: 'Asset Manager',
      details: 'New laptop asset added to inventory system',
      changes: {
        status: { from: null, to: 'Active' },
        owner: { from: null, to: 'Vikram Nair' },
        location: { from: null, to: 'Floor 3 - IT Department' }
      },
      ipAddress: '192.168.1.89',
      sessionId: 'sess_jkl012'
    },
    {
      id: 5,
      timestamp: '2025-08-18 23:17:33',
      action: 'Audit Report Generated',
      assetId: 'System Generated',
      user: 'System',
      userRole: 'Automated Process',
      details: 'Quarterly audit report generated for Q3 2025',
      changes: {
        reportStatus: { from: 'In Progress', to: 'Completed' }
      },
      ipAddress: 'System',
      sessionId: 'sys_auto001'
    }
  ];

  const getActionIcon = (action) => {
    switch (action) {
      case 'Asset Verified':
        return 'CheckCircle';
      case 'Bulk Status Update':
        return 'RefreshCw';
      case 'Exception Escalated':
        return 'AlertTriangle';
      case 'Asset Created':
        return 'Plus';
      case 'Audit Report Generated':
        return 'FileText';
      default:
        return 'Activity';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'Asset Verified':
        return 'text-success';
      case 'Bulk Status Update':
        return 'text-accent';
      case 'Exception Escalated':
        return 'text-warning';
      case 'Asset Created':
        return 'text-primary';
      case 'Audit Report Generated':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date?.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      }),
      time: date?.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      })
    };
  };

  const filteredTrail = auditTrail?.filter(entry => {
    const matchesSearch = searchTerm === '' || 
      entry?.assetId?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      entry?.user?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      entry?.action?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesFilter = filterType === 'all' || 
      entry?.action?.toLowerCase()?.includes(filterType?.toLowerCase());
    
    return matchesSearch && matchesFilter;
  });

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle">
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-card-foreground">Audit Trail</h3>
            <p className="text-sm text-muted-foreground">
              Complete verification history for SOX compliance
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm">
              <Icon name="Download" size={16} className="mr-2" />
              Export Trail
            </Button>
            <Button variant="outline" size="sm">
              <Icon name="Filter" size={16} className="mr-2" />
              Advanced Filter
            </Button>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          <div className="flex-1 max-w-md">
            <Input
              type="search"
              placeholder="Search by Asset ID, User, or Action..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e?.target?.value)}
              className="px-3 py-2 border border-border rounded-md text-sm bg-input text-foreground focus:ring-2 focus:ring-ring focus:border-ring"
            >
              <option value="all">All Actions</option>
              <option value="verified">Verified</option>
              <option value="escalated">Escalated</option>
              <option value="created">Created</option>
              <option value="updated">Updated</option>
            </select>
          </div>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        <div className="divide-y divide-border">
          {filteredTrail?.map((entry) => {
            const timestamp = formatTimestamp(entry?.timestamp);
            return (
              <div key={entry?.id} className="p-6 hover:bg-muted/30 transition-smooth">
                <div className="flex items-start space-x-4">
                  <div className={`mt-1 ${getActionColor(entry?.action)}`}>
                    <Icon name={getActionIcon(entry?.action)} size={20} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <h4 className="font-medium text-card-foreground">{entry?.action}</h4>
                        <span className="text-sm text-muted-foreground">
                          Asset: {entry?.assetId}
                        </span>
                      </div>
                      <div className="text-right text-sm text-muted-foreground">
                        <div>{timestamp?.date}</div>
                        <div>{timestamp?.time}</div>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-3">
                      {entry?.details}
                    </p>
                    
                    {entry?.changes && Object.keys(entry?.changes)?.length > 0 && (
                      <div className="bg-muted/50 rounded-md p-3 mb-3">
                        <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                          Changes Made
                        </h5>
                        <div className="space-y-1">
                          {Object.entries(entry?.changes)?.map(([field, change]) => (
                            <div key={field} className="flex items-center text-xs">
                              <span className="font-medium text-card-foreground capitalize">
                                {field?.replace(/([A-Z])/g, ' $1')?.trim()}:
                              </span>
                              <span className="ml-2 text-muted-foreground">
                                {change?.from ? `"${change?.from}"` : 'null'} → "{change?.to}"
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <div className="flex items-center space-x-4">
                        <span>
                          <Icon name="User" size={12} className="inline mr-1" />
                          {entry?.user} ({entry?.userRole})
                        </span>
                        <span>
                          <Icon name="Globe" size={12} className="inline mr-1" />
                          {entry?.ipAddress}
                        </span>
                      </div>
                      <span>Session: {entry?.sessionId}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="p-4 border-t border-border bg-muted/20">
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Showing {filteredTrail?.length} of {auditTrail?.length} entries</span>
          <span>All times in UTC. Retention period: 7 years</span>
        </div>
      </div>
    </div>
  );
};

export default AuditTrailPanel;